import { nanoid } from 'nanoid';
import { db } from './indexDB/database';
import { now } from '@/lib/utils';
import { Collection, Section } from '@/features/collections/types/collection.types';
import { OrderOption, Page, PageMetadata } from '@/features/pages/types/page.types';
import pageRepository, { getPageMetadataProps } from '@/features/pages/repositories/PageRepository';

class LocalDBService {
  async getLastSyncTime(): Promise<number> {
    const metadata = await db.syncMetadata.get('global');
    return metadata?.lastSyncedAt || 0;
  }

  async getCollectionsSince(syncTime: number | null) {
    const collections = await db.collections
      .where('updatedAt')
      .above(syncTime || 0)
      .toArray();

    return collections.map(n => ({
      ...n,
      createdAt: ((n.createdAt / 1000) | 0) * 1000,
      updatedAt: ((n.updatedAt / 1000) | 0) * 1000,
      archivedAt: n.archivedAt ? ((n.archivedAt / 1000) | 0) * 1000 : 0,
    }));
  }

  async getNotesSince(syncTime: number | null) {
    const notes = await db.pages
      .where('updatedAt')
      .above(syncTime || 0)
      .toArray();

    return notes.map(n => ({
      ...n,
      createdAt: ((n.createdAt / 1000) | 0) * 1000,
      updatedAt: ((n.updatedAt / 1000) | 0) * 1000,
      archivedAt: n.archivedAt ? ((n.archivedAt / 1000) | 0) * 1000 : 0,
    }));
  }

  async updateLastSyncTime(timestamp: number): Promise<void> {
    await db.syncMetadata.put({
      id: 'global',
      lastSyncedAt: timestamp,
      deviceId: (await db.syncMetadata.get('global'))?.deviceId || nanoid(),
    });
  }

  async getAllPages(orderBy: OrderOption = 'updatedAt'): Promise<Page[]> {
    if (orderBy !== 'orderNumber') {
      return db.pages.where('archivedAt').equals(0).reverse().sortBy(orderBy);
    }
    return db.pages.where('archivedAt').equals(0).sortBy(orderBy);
  }

  async getPagesMetadata(
    orderBy: OrderOption = 'updatedAt',
    filters: string[] = []
  ): Promise<PageMetadata[]> {
    let query = db.pagesMetadata
      .where('archivedAt')
      .equals(0)
      .and(page => !page.collectionId);
    if (filters.length) {
      query = query.and(page => {
        if (!page.tags) return false;
        const filterSet = new Set(filters);
        return page.tags.some(tag => filterSet.has(tag));
      });
    }
    const pages = await (orderBy === 'orderNumber'
      ? query.sortBy(orderBy)
      : query.reverse().sortBy(orderBy));

    return pages;
  }

  async getPageById(id: string): Promise<Page | undefined> {
    return db.pages.get(id);
  }

  async addPage(pageData?: Partial<Page>): Promise<string> {
    return pageRepository.addPage(pageData);
  }

  async addDefaultPages() {
    const hasPages = await db.pages.count();
    if (hasPages) {
      return;
    }
  }

  async updatePage(id: string, patch: Partial<Page>) {
    return pageRepository.updatePage(id, patch);
  }

  async updateIsPublic(id: string, isPublic: boolean) {
    return this.updatePage(id, { isPublic: isPublic });
  }

  async archivePage(id: string) {
    return this.updatePage(id, { archivedAt: Date.now() });
  }

  async unarchivePage(id: string) {
    return pageRepository.unarchivePage(id);
  }

  async deletePage(id: string): Promise<void> {
    return pageRepository.deletePage(id);
  }

  async searchPages(
    query: string,
    archived: boolean = false,
    collectionId?: string
  ): Promise<PageMetadata[]> {
    const lowercaseQuery = query.toLowerCase();

    let pagesQuery = db.pagesMetadata.reverse();
    pagesQuery = pagesQuery.and(page => (archived ? !!page.archivedAt : !page.archivedAt));
    if (collectionId) {
      pagesQuery = pagesQuery.and(page => page.collectionId === collectionId);
    }
    pagesQuery = pagesQuery.and(page => page.title.toLowerCase().includes(lowercaseQuery));
    return await pagesQuery.sortBy('updatedAt');
  }

  async getArchivedPages(collectionId?: string): Promise<PageMetadata[]> {
    const query = db.pagesMetadata.where('archivedAt').notEqual(0);

    if (collectionId) {
      query.and(page => page.collectionId === collectionId);
    }
    return query.reverse().sortBy('updatedAt');
  }

  async cleanupOldArchivedPages(): Promise<void> {
    return pageRepository.cleanupOldArchivedPages();
  }

  async bulkUpsertPages(pages: Page[]): Promise<void> {
    return pageRepository.bulkUpsertPages(pages);
  }

  async bulkDeletePages(pages: Page[]): Promise<void> {
    try {
      await pageRepository.bulkDeletePages(pages);
    } catch (error) {
      console.warn(
        'Error deleting pages:',
        pages.map(p => p.id),
        error
      );
    }
  }

  async getCollectionById(id: string): Promise<Collection | undefined> {
    return db.collections.get(id);
  }

  async getCollections(): Promise<Collection[]> {
    return db.collections.where('archivedAt').equals(0).reverse().sortBy('updatedAt');
  }

  async addCollection(): Promise<Collection> {
    const collectionId = nanoid();
    const sectionId = nanoid();
    const sections = [{ id: sectionId, title: 'Section 1', orderNumber: 0 }] as Section[];
    const newCollection: Collection = {
      id: collectionId,
      title: 'Untitled Collection',
      createdAt: now(),
      updatedAt: now(),
      archivedAt: 0,
      sections,
    };
    await db.collections.add(newCollection);
    await this.addPage({ collectionId, sectionId, orderNumber: 0 });
    return newCollection;
  }

  async archiveCollectionData(collection: Collection, sectionId?: string) {
    const pages = await this.getCollectionPageMetadata(collection.id, sectionId);
    const updates = pages.map(page => this.archivePage(page.id));
    await Promise.all(updates);
    const collectionUpdates: Partial<Collection> = { updatedAt: now() };
    if (!sectionId) {
      collectionUpdates.archivedAt = now();
    }
    await db.collections.update(collection.id, collectionUpdates);
  }

  async getCollectionPageMetadata(collectionId: string, sectionId?: string) {
    const collection = await this.getCollectionById(collectionId);
    if (!collection) return [];
    const query = db.pagesMetadata
      .where('collectionId')
      .equals(collectionId)
      .and(page => (!collection.archivedAt ? !page.archivedAt : !!page.archivedAt));

    if (sectionId) {
      query.and(page => page.sectionId === sectionId);
    }

    const pages = await query.sortBy('orderNumber');
    return pages.map(getPageMetadataProps);
  }

  searchCollections(searchQuery: string) {
    return db.collections
      .where('archivedAt')
      .equals(0)
      .and(collection => collection.title.toLowerCase().includes(searchQuery.toLowerCase()))
      .reverse()
      .sortBy('updatedAt');
  }

  updateCollection(collectionId: string, collection: Partial<Collection>) {
    return db.collections.update(collectionId, { ...collection, updatedAt: now() });
  }

  bulkUpsertCollections(collections: Collection[]) {
    return db.collections.bulkPut(collections);
  }

  bulkDeleteCollections(collections: Collection[]) {
    return db.collections.bulkDelete(collections.map(c => c.id));
  }
}

const DBService = new LocalDBService();
export default DBService;
